if (!customElements.get('side-cart')) {
  customElements.define(
    'side-cart',
    class SideCart extends HTMLElement {
      constructor() {
        super();
        this.panel = this.querySelector('.side-cart__panel');

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('submit', this.onSubmit.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
        this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
        this.bindCartIcon();
        this.startTimer();
        this.expireDiscountFromPreviousVisit();
      }

      // Discount codes only last for the browser session: a session cookie marks codes applied in this visit
      static DISCOUNT_SESSION_COOKIE = 'side_cart_discount_session';

      // Session cookies (no expiry) are dropped when the browser is closed
      hasSessionCookie(value) {
        return document.cookie.split('; ').includes(`${SideCart.DISCOUNT_SESSION_COOKIE}=${value}`);
      }

      setSessionCookie(value) {
        document.cookie = `${SideCart.DISCOUNT_SESSION_COOKIE}=${value}; path=/; SameSite=Lax`;
      }

      startDiscountSession() {
        this.setSessionCookie('applied');
      }

      // First page of a new browser session: drop codes left on the cart from an earlier visit (checked once per session)
      expireDiscountFromPreviousVisit() {
        if (this.hasSessionCookie('applied') || this.hasSessionCookie('checked')) return;
        this.setSessionCookie('checked');
        fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            if (cart.discount_codes?.length) this.clearDiscounts();
          })
          .catch((error) => console.error(error));
      }

      clearDiscounts() {
        return fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            discount: '',
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((state) => this.renderSections(state))
          .catch((error) => console.error(error));
      }

      startTimer() {
        const storageKey = 'side-cart-reserved-until';
        const getDeadline = (minutes) => {
          if (this.reservedUntil) return this.reservedUntil;
          try {
            this.reservedUntil = parseInt(sessionStorage.getItem(storageKey), 10) || 0;
          } catch (e) {}
          if (!this.reservedUntil) {
            this.reservedUntil = Date.now() + minutes * 60000;
            try {
              sessionStorage.setItem(storageKey, this.reservedUntil);
            } catch (e) {}
          }
          return this.reservedUntil;
        };

        const tick = () => {
          const timer = this.querySelector('[data-side-cart-timer]');
          if (!timer) return;
          const minutes = parseInt(timer.dataset.minutes, 10) || 10;
          const seconds = Math.max(0, Math.round((getDeadline(minutes) - Date.now()) / 1000));
          const pad = (value) => String(value).padStart(2, '0');
          timer.textContent = `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;

          const expired = seconds === 0;
          const reserved = this.querySelector('[data-side-cart-reserved]');
          const expiredText = this.querySelector('[data-side-cart-expired]');
          if (reserved) reserved.hidden = expired;
          if (expiredText) expiredText.hidden = !expired;
        };

        tick();
        setInterval(tick, 1000);
      }

      connectedCallback() {
        if (typeof subscribe !== 'function') return;
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'side-cart' || event.source === 'product-form') return;
          this.refresh();
        });
      }

      disconnectedCallback() {
        this.cartUpdateUnsubscriber?.();
      }

      refresh() {
        const sections = this.getSectionsToRender().map((section) => section.id).join(',');
        return fetch(`${routes.cart_url}.js?sections=${sections}`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((state) => this.renderSections(state))
          .catch((error) => console.error(error));
      }

      isEnabled() {
        return true;
      }

      bindCartIcon() {
        document.addEventListener(
          'click',
          (event) => {
            const cartLink = event.target.closest('#cart-icon-bubble');
            if (!cartLink || !this.isEnabled()) return;
            event.preventDefault();
            this.open(cartLink);
          },
          true
        );
      }

      onClick(event) {
        if (event.target.closest('[data-side-cart-close]')) {
          this.close();
          return;
        }

        const qtyButton = event.target.closest('[data-side-cart-qty]');
        if (qtyButton) this.updateQuantity(qtyButton.dataset.key, parseInt(qtyButton.dataset.sideCartQty, 10));

        const removeCode = event.target.closest('[data-side-cart-remove-code]');
        if (removeCode) {
          const codes = this.getAppliedCodes().filter((code) => code !== removeCode.dataset.sideCartRemoveCode);
          this.updateDiscounts(codes);
        }
      }

      onSubmit(event) {
        const form = event.target.closest('[data-side-cart-discount]');
        if (!form) return;
        event.preventDefault();

        const code = form.elements.discount.value.trim();
        if (!code) return;
        const codes = this.getAppliedCodes();
        if (codes.includes(code)) return;
        this.updateDiscounts([...codes, code], code);
      }

      getAppliedCodes() {
        const form = this.querySelector('[data-side-cart-discount]');
        return (form?.dataset.codes || '').split(',').filter(Boolean);
      }

      updateDiscounts(codes, newCode) {
        if (this.panel.classList.contains('is-loading')) return;
        this.panel.classList.add('is-loading');

        fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            discount: codes.join(','),
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((state) => {
            if (state.errors || state.description) throw new Error(state.description || state.errors);
            this.renderSections(state);

            const applied = (state.discount_codes || []).some(
              (discount) => discount.applicable && discount.code.toLowerCase() === newCode?.toLowerCase()
            );
            if (newCode && !applied) this.showDiscountError(newCode);
            if (applied) {
              this.startDiscountSession();
              this.showDiscountSuccess(newCode);
            }

            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'side-cart', cartData: state });
            }
          })
          .catch((error) => {
            console.error(error);
            if (newCode) this.showDiscountError(newCode);
          })
          .finally(() => this.panel.classList.remove('is-loading'));
      }

      onInput(event) {
        const input = event.target;
        if (!input.matches('.side-cart__discount-input')) return;
        // Editing the applied code means it is no longer the confirmed one
        input.classList.toggle('is-valid', !!input.defaultValue && input.value === input.defaultValue);
      }

      // Restart a one-shot CSS animation class on an element
      replayClass(element, className) {
        element.classList.remove(className);
        void element.offsetWidth;
        element.classList.add(className);
      }

      showDiscountSuccess(code) {
        const input = this.querySelector('.side-cart__discount-input');
        if (!input) return;
        input.value = input.defaultValue = code;
        input.classList.add('is-valid');
        this.replayClass(input, 'is-applied');
      }

      // Shake the field red, then clear the wrong code and restore the previously applied one (if any)
      showDiscountError(code) {
        const form = this.querySelector('[data-side-cart-discount]');
        if (!form) return;
        const input = form.elements.discount;
        const error = form.querySelector('.side-cart__discount-error');
        input.value = code;
        input.classList.remove('is-valid', 'is-applied');
        this.replayClass(input, 'is-invalid');
        error.hidden = false;

        clearTimeout(this.discountErrorTimer);
        this.discountErrorTimer = setTimeout(() => {
          input.classList.remove('is-invalid');
          error.hidden = true;
          input.value = input.defaultValue;
          input.classList.toggle('is-valid', !!input.defaultValue);
        }, 2000);
      }

      open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        if (this.classList.contains('active')) return;

        requestAnimationFrame(() => this.classList.add('active'));
        this.panel.addEventListener('transitionend', () => trapFocus(this.panel), { once: true });
        document.body.classList.add('overflow-hidden');
      }

      close() {
        if (!this.classList.contains('active')) return;
        this.classList.remove('active');
        removeTrapFocus(this.activeElement);
        document.body.classList.remove('overflow-hidden');
      }

      updateQuantity(key, quantity) {
        if (this.panel.classList.contains('is-loading')) return;
        this.panel.classList.add('is-loading');

        fetch(`${routes.cart_change_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: key,
            quantity: Math.max(0, quantity),
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((state) => {
            if (state.errors || state.description) throw new Error(state.description || state.errors);
            this.renderContents(state);
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'side-cart', cartData: state });
            }
            // Emptying the cart drops the discount code; it has to be entered again
            if (state.item_count === 0 && state.discount_codes?.length) return this.clearDiscounts();
          })
          .catch((error) => console.error(error))
          .finally(() => this.panel.classList.remove('is-loading'));
      }

      renderContents(parsedState) {
        this.renderSections(parsedState);
        this.open();
      }

      renderSections(parsedState) {
        const sections = parsedState.sections || {};

        const content = this.parseSection(sections['side-cart'], '#SideCartContent');
        if (content) this.querySelector('#SideCartContent').innerHTML = content.innerHTML;

        const bubble = this.parseSection(sections['cart-icon-bubble'], '.shopify-section');
        const cartLink = document.getElementById('cart-icon-bubble');
        if (bubble && cartLink) cartLink.innerHTML = bubble.innerHTML;
        if (bubble && cartLink) {
          cartLink.classList.remove('cart-icon--bump');
          void cartLink.offsetWidth;
          cartLink.classList.add('cart-icon--bump');
          setTimeout(() => cartLink.classList.remove('cart-icon--bump'), 900);
        }
      }

      parseSection(html, selector) {
        if (!html) return null;
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      }

      getSectionsToRender() {
        return [{ id: 'side-cart' }, { id: 'cart-icon-bubble' }];
      }

      setActiveElement(element) {
        this.activeElement = element;
      }
    }
  );
}
